import { useEffect, useRef, useState, useId, useCallback } from "react";
import { createPortal } from "react-dom";
import mermaid from "mermaid";
import { Maximize2, X, ZoomIn, ZoomOut, RotateCcw, Shrink, Download } from "lucide-react";

let mermaidInitialized = false;

function initMermaid(isDark: boolean) {
  mermaid.initialize({
    startOnLoad: false,
    theme: isDark ? "dark" : "default",
    securityLevel: "strict",
    fontFamily: "var(--font-sans)",
    flowchart: {
      htmlLabels: false,
      useMaxWidth: true,
      curve: "monotoneX",
      padding: 20,
      nodeSpacing: 80,
      rankSpacing: 70,
      wrappingWidth: 200,
    },
    themeVariables: isDark
      ? {
          primaryColor: "#34d399",
          primaryTextColor: "#f9fafb",
          primaryBorderColor: "#059669",
          lineColor: "#6b7280",
          secondaryColor: "#1f2937",
          tertiaryColor: "#111827",
        }
      : {
          primaryColor: "#a7f3d0",
          primaryTextColor: "#1f2937",
          primaryBorderColor: "#059669",
          lineColor: "#9ca3af",
          secondaryColor: "#f0fdf4",
          tertiaryColor: "#ecfdf5",
        },
  });
  mermaidInitialized = true;
}

function sanitizeMermaidChart(chart: string): string {
  let cleaned = chart.trim();
  cleaned = cleaned.replace(/\[([^\]"]*\([^\]]*\)[^\]"]*)\]/g, '["$1"]');
  cleaned = cleaned.replace(/^(flowchart)\s*$/m, "$1 TD");
  cleaned = cleaned.replace(/[\u{1F300}-\u{1F9FF}]/gu, "");
  return cleaned;
}

function makeResponsiveSvg(svgStr: string): string {
  const hasViewBox = /<svg[^>]*\sviewBox=/i.test(svgStr);
  let output = svgStr;

  if (hasViewBox) {
    output = output
      .replace(/(<svg[^>]*?)(\s+width=["'][^"']*["'])/i, "$1")
      .replace(/(<svg[^>]*?)(\s+height=["'][^"']*["'])/i, "$1");
  }

  output = output.replace(/(<svg[^>]*?)(\s+style=["'][^"']*["'])/i, "$1");
  return output.replace(/<svg/, '<svg preserveAspectRatio="xMidYMid meet" style="display:block;max-width:100%;height:auto"');
}

interface SvgDimensions {
  width: number;
  height: number;
}

function extractSvgDimensions(svgStr: string): SvgDimensions | null {
  const doc = new DOMParser().parseFromString(svgStr, "image/svg+xml");
  const svg = doc.querySelector("svg");
  if (!svg) return null;

  const viewBox = svg.getAttribute("viewBox");
  if (viewBox) {
    const parts = viewBox.split(/\s+/).map(Number);
    const w = parts[2];
    const h = parts[3];
    if (parts.length === 4 && w !== undefined && h !== undefined && Number.isFinite(w) && Number.isFinite(h)) {
      return { width: Math.max(w, 1), height: Math.max(h, 1) };
    }
  }

  const width = Number.parseFloat(svg.getAttribute("width") || "");
  const height = Number.parseFloat(svg.getAttribute("height") || "");
  if (Number.isFinite(width) && Number.isFinite(height) && width > 0 && height > 0) {
    return { width, height };
  }

  return null;
}

const MIN_ZOOM = 0.05;
const MAX_ZOOM = 8;
const DEFAULT_FULLSCREEN_ZOOM = 2.5;
const DIAGRAM_PLACEHOLDER_HEIGHT = 280;

let renderCounter = 0;

interface MermaidDiagramProps {
  chart: string;
}

export function MermaidDiagram({ chart }: MermaidDiagramProps) {
  const [error, setError] = useState(false);
  const [svg, setSvg] = useState<string>("");
  const [svgDimensions, setSvgDimensions] = useState<SvgDimensions>({ width: 1200, height: 800 });
  const [isFullscreen, setIsFullscreen] = useState(false);
  const fullscreenRef = useRef(false);
  const [zoom, setZoom] = useState(1);
  const [pan, setPan] = useState({ x: 0, y: 0 });
  const [isPanning, setIsPanning] = useState(false);
  const panStart = useRef({ x: 0, y: 0, panX: 0, panY: 0 });
  const reactId = useId();
  const idRef = useRef(`mmd-${reactId.replace(/:/g, "")}-${renderCounter++}`);
  const fullscreenViewportRef = useRef<HTMLDivElement>(null);
  const [lockedHeight, setLockedHeight] = useState<number | null>(null);
  const containerRef = useRef<HTMLDivElement>(null);

  // Keep ref in sync to prevent stale closures from closing fullscreen
  useEffect(() => {
    fullscreenRef.current = isFullscreen;
  }, [isFullscreen]);

  useEffect(() => {
    let cancelled = false;
    const isDark = document.documentElement.classList.contains("dark");

    if (!mermaidInitialized) {
      initMermaid(isDark);
    }

    const renderDiagram = async () => {
      const sanitized = sanitizeMermaidChart(chart);
      try {
        await mermaid.parse(sanitized);
        const { svg: renderedSvg } = await mermaid.render(idRef.current, sanitized);
        if (!cancelled) {
          setSvg(renderedSvg);
          setSvgDimensions(extractSvgDimensions(renderedSvg) ?? { width: 1200, height: 800 });
          setError(false);
        }
      } catch (e) {
        console.warn("Mermaid render failed:", e);
        if (!cancelled) {
          setError(true);
        }
        const tempEl = document.getElementById(idRef.current);
        if (tempEl) tempEl.remove();
      }
    };

    renderDiagram();

    return () => {
      cancelled = true;
    };
  }, [chart]);

  // Lock container height after SVG renders to prevent scroll jumps
  useEffect(() => {
    if (svg && containerRef.current && lockedHeight === null) {
      // Use rAF to measure after paint
      requestAnimationFrame(() => {
        if (containerRef.current) {
          setLockedHeight(containerRef.current.getBoundingClientRect().height);
        }
      });
    }
  }, [svg, lockedHeight]);

  // Close fullscreen on Escape only
  useEffect(() => {
    if (!isFullscreen) return;
    const handleKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") setIsFullscreen(false);
    };
    document.addEventListener("keydown", handleKey);
    document.body.style.overflow = "hidden";
    return () => {
      document.removeEventListener("keydown", handleKey);
      document.body.style.overflow = "";
    };
  }, [isFullscreen]);

  const clampZoom = useCallback((value: number) => Math.min(MAX_ZOOM, Math.max(MIN_ZOOM, value)), []);

  const fitToScreen = useCallback(() => {
    const viewport = fullscreenViewportRef.current;
    if (!viewport) {
      setPan({ x: 0, y: 0 });
      setZoom(1);
      return;
    }

    const viewportRect = viewport.getBoundingClientRect();
    if (viewportRect.width === 0 || viewportRect.height === 0) {
      setPan({ x: 0, y: 0 });
      setZoom(1);
      return;
    }

    const scaleX = (viewportRect.width * 0.9) / svgDimensions.width;
    const scaleY = (viewportRect.height * 0.9) / svgDimensions.height;
    const fitScale = clampZoom(Math.min(scaleX, scaleY));

    setPan({ x: 0, y: 0 });
    setZoom(Number.isFinite(fitScale) ? fitScale : 1);
  }, [clampZoom, svgDimensions.height, svgDimensions.width]);

  const downloadPng = useCallback(() => {
    if (!svg) return;

    const triggerDownload = (href: string, fileName: string) => {
      const link = document.createElement("a");
      link.download = fileName;
      link.href = href;
      document.body.appendChild(link);
      link.click();
      link.remove();
    };

    const parser = new DOMParser();
    const doc = parser.parseFromString(svg, "image/svg+xml");
    const svgEl = doc.querySelector("svg");
    if (!svgEl) return;

    const pad = 24;
    const width = Math.max(svgDimensions.width + pad * 2, 1);
    const height = Math.max(svgDimensions.height + pad * 2, 1);

    if (!svgEl.getAttribute("viewBox")) {
      svgEl.setAttribute("viewBox", `0 0 ${svgDimensions.width} ${svgDimensions.height}`);
    }
    svgEl.setAttribute("viewBox", `${-pad} ${-pad} ${width} ${height}`);
    svgEl.setAttribute("width", String(width));
    svgEl.setAttribute("height", String(height));
    svgEl.removeAttribute("style");

    const svgString = new XMLSerializer().serializeToString(svgEl);
    const encodedSvg = `data:image/svg+xml;charset=utf-8,${encodeURIComponent(svgString)}`;

    const img = new Image();
    img.crossOrigin = "anonymous";

    img.onload = () => {
      const scale = 2;
      const canvas = document.createElement("canvas");
      canvas.width = Math.round(width * scale);
      canvas.height = Math.round(height * scale);
      const ctx = canvas.getContext("2d");

      if (!ctx) {
        triggerDownload(encodedSvg, "diagram.svg");
        return;
      }

      ctx.fillStyle = document.documentElement.classList.contains("dark") ? "hsl(222 47% 11%)" : "hsl(0 0% 100%)";
      ctx.fillRect(0, 0, canvas.width, canvas.height);
      ctx.drawImage(img, 0, 0, canvas.width, canvas.height);

      try {
        const pngData = canvas.toDataURL("image/png");
        triggerDownload(pngData, "diagram.png");
      } catch {
        triggerDownload(encodedSvg, "diagram.svg");
      }
    };

    img.onerror = () => {
      triggerDownload(encodedSvg, "diagram.svg");
    };

    img.src = encodedSvg;
  }, [svg, svgDimensions.height, svgDimensions.width]);

  // Set initial zoom only when entering fullscreen (not on svg changes)
  const prevFullscreen = useRef(false);
  useEffect(() => {
    if (isFullscreen && !prevFullscreen.current && svg) {
      setPan({ x: 0, y: 0 });
      setZoom(clampZoom(DEFAULT_FULLSCREEN_ZOOM));
    }
    prevFullscreen.current = isFullscreen;
  }, [clampZoom, isFullscreen, svg]);

  // Mouse wheel zoom in fullscreen
  const handleWheel = useCallback((e: React.WheelEvent) => {
    e.preventDefault();
    setZoom((z) => clampZoom(e.deltaY > 0 ? z / 1.15 : z * 1.15));
  }, [clampZoom]);

  // Pan handlers
  const handleMouseDown = useCallback((e: React.MouseEvent) => {
    setIsPanning(true);
    panStart.current = { x: e.clientX, y: e.clientY, panX: pan.x, panY: pan.y };
  }, [pan.x, pan.y]);

  const handleMouseMove = useCallback((e: React.MouseEvent) => {
    if (!isPanning) return;
    setPan({
      x: panStart.current.panX + (e.clientX - panStart.current.x),
      y: panStart.current.panY + (e.clientY - panStart.current.y),
    });
  }, [isPanning]);

  const handleMouseUp = useCallback(() => {
    setIsPanning(false);
  }, []);

  const openFullscreen = useCallback(() => {
    setIsFullscreen(true);
  }, []);

  const closeFullscreen = useCallback(() => {
    setIsFullscreen(false);
  }, []);

  if (error) {
    return (
      <pre className="text-sm bg-muted/30 rounded-lg p-4 overflow-x-auto border border-border">
        <code>{chart}</code>
      </pre>
    );
  }

  if (!svg) {
    return (
      <div
        className="my-6 flex justify-center items-center rounded-lg border border-border/50 bg-muted/20 p-3"
        style={{ minHeight: DIAGRAM_PLACEHOLDER_HEIGHT }}
      >
        <div className="h-6 w-6 animate-spin rounded-full border-2 border-primary border-t-transparent" />
      </div>
    );
  }

  const responsiveSvg = makeResponsiveSvg(svg);

  const fullscreenOverlay = isFullscreen
    ? createPortal(
        <div
          className="fixed inset-0 z-[9999] bg-background/95 backdrop-blur-xs flex flex-col"
        >
          {/* Toolbar */}
          <div
            className="flex items-center justify-between gap-3 px-4 py-3 border-b border-border bg-background/80"
            onMouseDown={(e) => e.stopPropagation()}
          >
            <span className="text-sm font-medium text-muted-foreground truncate">
              Diagram. scroll to zoom, drag to pan
            </span>
            <div className="flex items-center gap-1 shrink-0">
              <button
                type="button"
                onClick={() => setZoom((z) => clampZoom(z / 1.2))}
                className="p-2 rounded-md hover:bg-muted text-muted-foreground hover:text-foreground transition-colors"
                aria-label="Zoom out"
              >
                <ZoomOut className="w-4 h-4" />
              </button>
              <span className="text-xs text-muted-foreground min-w-[3.5rem] text-center tabular-nums">
                {Math.round(zoom * 100)}%
              </span>
              <button
                type="button"
                onClick={() => setZoom((z) => clampZoom(z * 1.2))}
                className="p-2 rounded-md hover:bg-muted text-muted-foreground hover:text-foreground transition-colors"
                aria-label="Zoom in"
              >
                <ZoomIn className="w-4 h-4" />
              </button>
              <button
                type="button"
                onClick={() => {
                  setZoom(clampZoom(DEFAULT_FULLSCREEN_ZOOM));
                  setPan({ x: 0, y: 0 });
                }}
                className="p-2 rounded-md hover:bg-muted text-muted-foreground hover:text-foreground transition-colors"
                aria-label="Reset zoom"
              >
                <RotateCcw className="w-4 h-4" />
              </button>
              <button
                type="button"
                onClick={fitToScreen}
                className="p-2 rounded-md hover:bg-muted text-muted-foreground hover:text-foreground transition-colors"
                aria-label="Fit to screen"
                title="Fit to screen"
              >
                <Shrink className="w-4 h-4" />
              </button>
              <button
                type="button"
                onClick={downloadPng}
                className="p-2 rounded-md hover:bg-muted text-muted-foreground hover:text-foreground transition-colors"
                aria-label="Download PNG"
                title="Download as PNG"
              >
                <Download className="w-4 h-4" />
              </button>
              <div className="w-px h-5 bg-border mx-1" />
              <button
                type="button"
                onClick={closeFullscreen}
                className="p-2 rounded-md hover:bg-muted text-muted-foreground hover:text-foreground transition-colors"
                aria-label="Close"
              >
                <X className="w-5 h-5" />
              </button>
            </div>
          </div>

          {/* Diagram area. pannable & zoomable */}
          <div
            ref={fullscreenViewportRef}
            className="flex-1 overflow-hidden relative"
            style={{ cursor: isPanning ? "grabbing" : "grab" }}
            onMouseDown={(e) => {
              e.stopPropagation();
              handleMouseDown(e);
            }}
            onMouseMove={handleMouseMove}
            onMouseUp={handleMouseUp}
            onMouseLeave={handleMouseUp}
            onWheel={handleWheel}
          >
            <div
              className="absolute inset-0 flex items-center justify-center"
              style={{
                transform: `translate(${pan.x}px, ${pan.y}px) scale(${zoom})`,
                transformOrigin: "center center",
                transition: isPanning ? "none" : "transform 0.15s ease-out",
                willChange: "transform",
              }}
            >
              <div
                className="[&_svg]:block"
                dangerouslySetInnerHTML={{ __html: svg }}
                style={{ width: svgDimensions.width, height: svgDimensions.height }}
              />
            </div>
          </div>
        </div>,
        document.body
      )
    : null;

  return (
    <>
      <div
        ref={containerRef}
        className="mermaid-container my-6 relative group rounded-lg border border-border/50 bg-muted/20 p-3"
        style={{
          contain: "layout style",
          overflow: "hidden",
        }}
      >
        <div
          className="flex justify-center overflow-auto rounded-md border border-border/50 bg-background/40 p-2 [&_svg]:max-w-full [&_svg]:h-auto"
          dangerouslySetInnerHTML={{ __html: responsiveSvg }}
        />
        <button
          type="button"
          onClick={openFullscreen}
          className="absolute top-2 right-2 p-1.5 rounded-md bg-background/80 border border-border text-muted-foreground hover:text-foreground opacity-0 group-hover:opacity-100 transition-opacity"
          aria-label="Fullscreen"
        >
          <Maximize2 className="w-4 h-4" />
        </button>
      </div>

      {fullscreenOverlay}
    </>
  );
}
